import { WorkflowConfigField, WorkflowConfigFieldType } from '@monorepo/interfaces';

export class WorkflowFormTransformerOperations {
    /**
     * Converts a field value to either a string or string array depending on field configuration.
     *
     * @description
     * If the field is configured as a multi-select, transforms the value from a comma-separated string to a string array.
     * Otherwise, returns the value as a string.
     *
     * @param workflowFieldConfig {WorkflowConfigField} The field configuration.
     * @param fieldValue {string} The raw field value.
     * @returns {string | string[]} The processed field value.
     */
    public static getFieldValueStringOrCommaSeparatedString(
        workflowFieldConfig: WorkflowConfigField,
        fieldValue: string | string[]
    ): string | string[] {
        if (this.checkFieldValueIsArray(workflowFieldConfig)) {
            return this.transformFieldValueFromArrayToString(fieldValue as string[]);
        } else if (this.checkFieldValueIsBoolean(workflowFieldConfig)) {
            return fieldValue ? 'true' : 'false';
        } else {
            return fieldValue;
        }
    }

    /**
     * Converts a field value to either a string or string array depending on field configuration.
     *
     * @description
     * If the field is configured as a multi-select, transforms the value from a comma-separated string to a string array.
     * Otherwise, returns the value as a string.
     *
     * @param workflowFieldConfig {WorkflowConfigField} The field configuration.
     * @param fieldValue {string} The raw field value.
     * @returns {string | string[] | boolean} The processed field value.
     */
    public static getFieldValueStringOrStringArray(
        workflowFieldConfig: WorkflowConfigField,
        fieldValue: string
    ): string | string[] | boolean {
        if (this.checkFieldValueIsArray(workflowFieldConfig)) {
            return this.transformFieldValueFromStringToArray(fieldValue);
        } else if (fieldValue === 'true' || fieldValue === 'false') {
            return fieldValue === 'true';
        } else if (workflowFieldConfig.fieldType === WorkflowConfigFieldType.DATE_RANGE && fieldValue) {
            return this.normalizeToDateInputFormat(fieldValue);
        } else {
            return fieldValue;
        }
    }

    private static normalizeToDateInputFormat(value: string): string {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        return date.toISOString().slice(0, 10);
    }

    /**
     * Transforms a comma-separated string field value into a string array.
     *
     * @description
     * Used for multi-select fields where the value is stored as a comma-separated string.
     * Returns an empty array if the value is not a string or is falsy.
     *
     * @param fieldValue {string} The comma-separated string value.
     * @returns {string[]} The array of selected values.
     */
    private static transformFieldValueFromStringToArray(fieldValue: string): string[] {
        if (!fieldValue || typeof fieldValue !== 'string') {
            return [];
        }
        return fieldValue.split(', ');
    }

    /**
     * Transforms a string array into comma-separated string.
     *
     * @description
     * Used for multi-select fields where the value is stored as a string array.
     * Returns an empty string if the value is not a string array or is falsy.
     *
     * @param fieldValue {string[]} The array of string values.
     * @returns {string} The comma-separated string of selected values.
     */
    private static transformFieldValueFromArrayToString(fieldValue: string[]): string {
        if (!fieldValue || !Array.isArray(fieldValue)) {
            return '';
        }
        return fieldValue.join(', ');
    }

    /**
     * Checks if a field configuration represents a multi-select field (can be extended to other types in the future)
     *
     * @description
     * Determines if the field type is SELECT and is marked as multi-select (can be extended to other types in the future)
     *
     * @param field {WorkflowConfigField} The field configuration.
     * @returns {boolean} True if the field is a multi-select SELECT, false otherwise.
     */
    private static checkFieldValueIsArray(field: WorkflowConfigField): boolean {
        return field.fieldType === WorkflowConfigFieldType.SELECT && field.isMultiSelect;
    }

    /**
     * Checks if a field configuration represents a checkbox field
     *
     * @description
     * Determines if the field type is CHECKBOX
     *
     * @param field {WorkflowConfigField} The field configuration.
     * @returns {boolean} True if the field is a checkbox, false otherwise.
     */
    private static checkFieldValueIsBoolean(field: WorkflowConfigField): boolean {
        return field.fieldType === WorkflowConfigFieldType.INPUT && field.type === 'boolean';
    }
}
